// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import './ImageCarousel.css';

/**
 * Universal image carousel component
 * @param {Object} props - Component props
 * @param {Array<string>} props.images - Array of image URLs to display
 * @returns {JSX.Element} - A carousel displaying images with navigation
 */
const ImageCarousel = ({ images }) => {
  // Initialize Embla carousel with proper options
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
    draggable: true,
    containScroll: 'trimSnaps'
  });

  // Initialize lightbox carousel with same settings
  const [lightboxRef, lightboxApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    skipSnaps: false,
    draggable: true,
    containScroll: 'trimSnaps'
  });

  // Component state
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Handle Escape key to close lightbox
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsLightboxOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // Update state on carousel selection
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  // Navigate to a specific slide
  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
      if (lightboxApi) lightboxApi.scrollTo(index);
    },
    [emblaApi, lightboxApi]
  );

  // Set up event listeners for the carousel
  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', onSelect);
      onSelect(); // Initialize state
      emblaApi.reInit(); // Force a reinit to ensure correct layout
    }
    return () => {
      if (emblaApi) {
        emblaApi.off('select', onSelect);
      }
    };
  }, [emblaApi, onSelect]);

  // Sync lightbox with main carousel when opened
  useEffect(() => {
    if (isLightboxOpen && lightboxApi) {
      // First force a reInit to ensure the lightbox is properly setup
      lightboxApi.reInit();
      // Then scroll to the selected index
      lightboxApi.scrollTo(selectedIndex);
      // And do another reInit after a brief delay to ensure sizing is correct
      setTimeout(() => {
        lightboxApi.reInit();
      }, 100);
    }
  }, [isLightboxOpen, lightboxApi, selectedIndex]);

  // Don't render anything if there are no images
  if (!images || images.length === 0) {
    return null;
  }

  // Handler for opening the lightbox
  const openLightbox = (index) => {
    setSelectedIndex(index);
    setIsLightboxOpen(true);
  };

  // Navigation handlers
  const scrollPrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
    if (isLightboxOpen && lightboxApi) lightboxApi.scrollPrev();
  };

  const scrollNext = () => {
    if (emblaApi) emblaApi.scrollNext();
    if (isLightboxOpen && lightboxApi) lightboxApi.scrollNext();
  };

  return (
    <div className='image-carousel-container'>
      {/* Main carousel */}
      <div className='image-carousel-viewport' ref={emblaRef}>
        <div className='embla__container'>
          {images.map((image, index) => (
            <div className='image-carousel-slide' key={index}>
              <img
                src={image}
                alt={`Slide ${index + 1}`}
                onClick={() => openLightbox(index)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons - only shown if there's more than one image */}
      {images.length > 1 && (
        <>
          <button
            type='button'
            className='image-carousel-button image-carousel-button-prev'
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          >
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
              <path d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' />
            </svg>
          </button>
          <button
            type='button'
            className='image-carousel-button image-carousel-button-next'
            onClick={scrollNext}
            disabled={!canScrollNext}
          >
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
              <path d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' />
            </svg>
          </button>

          {/* Dots indicator */}
          <div className='image-carousel-dots'>
            {images.map((_, index) => (
              <div
                key={index}
                className={`image-carousel-dot ${index === selectedIndex ? 'active' : ''}`}
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        </>
      )}

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className='image-lightbox'>
          <button
            type='button'
            className='image-lightbox-close'
            onClick={() => setIsLightboxOpen(false)}
          >
            ✕
          </button>

          <div className='image-lightbox-content'>
            <div className='image-carousel-viewport' ref={lightboxRef}>
              <div className='embla__container'>
                {images.map((image, index) => (
                  <div className='lightbox-slide' key={index}>
                    <img src={image} alt={`Slide ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>

            {images.length > 1 && (
              <>
                <button
                  type='button'
                  className='image-carousel-button image-carousel-button-prev'
                  onClick={() => lightboxApi?.scrollPrev()}
                >
                  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
                    <path d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' />
                  </svg>
                </button>
                <button
                  type='button'
                  className='image-carousel-button image-carousel-button-next'
                  onClick={() => lightboxApi?.scrollNext()}
                >
                  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
                    <path d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default ImageCarousel;
